import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, switchMap, tap, startWith, Observable } from 'rxjs';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { AsyncPipe, NgIf } from '@angular/common';

import { LoadingSpinnerComponent } from '../../../components/utils/loading-spinner.component';
import { DatacenterFilterApiService } from '../../../services/datacenter-filter-api.service';
import { SuggestiveListWithIdFilter } from '../../../models/datacenter-filters';

interface ItemValue {
	code: string | undefined;
	translation: string | undefined;
	extraField?: string;
}

@Component({
	selector: 'copo-suggestive-item-code-filter',
	templateUrl: './suggestive-item-code-filter.component.html',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		MatFormFieldModule,
		MatInputModule,
		MatAutocompleteModule,
		FormsModule,
		ReactiveFormsModule,
		MatIconModule,
		MatOptionModule,
		LoadingSpinnerComponent,
		AsyncPipe
	],
})
export class SuggestiveItemCodeFilterComponent implements OnInit {
	@Input() placeholder?: string;
	@Input() filterValue?: ItemValue;
	@Input() filter?: SuggestiveListWithIdFilter;
	suggestiveCtrl = new FormControl('', { nonNullable: true });

	filteredItems?: Observable<ItemValue[]>;
	isLoading = false;
	private readonly NO_VALUE_LABEL = 'No value';

	constructor(
		private readonly datacenterFilterApiService: DatacenterFilterApiService
	) {}

	ngOnInit() {
		if (this.filterValue) {
			this.suggestiveCtrl.setValue(this.getValue(this.filterValue));
		}
		this.filteredItems = this.suggestiveCtrl.valueChanges
			.pipe(
				startWith(this.suggestiveCtrl.value),
				debounceTime(300),
				distinctUntilChanged(),
				tap(query => {
					this.clearSelectionOnManualInput(query);
				}),
				tap(() => {
					this.isLoading = true;
				}),
				switchMap((query: string) => this.datacenterFilterApiService.getItemBySearchTextOrId(
					this.filter,
					this.stripParenthesizedSuffix(query),
					this.getParenthesizedSuffix(query),
					this.filterValue?.code || undefined
				)),
				tap(() => {
					this.isLoading = false;
				})
			);
	}

	truncateZeros(code: string) {
		return code.replace(/^0+/, '');
	}

	stripParenthesizedSuffix(value: string): string {
		const openParenIndex = value.lastIndexOf('(');
		if (openParenIndex === -1) {
			return value.trim();
		}
		return value.slice(0, openParenIndex).trim();
	}

	getParenthesizedSuffix(value: string): string | undefined {
		const openParenIndex = value.lastIndexOf('(');
		const closeParenIndex = value.lastIndexOf(')');
		if (openParenIndex === -1) {
			return undefined;
		}
		const endIndex = closeParenIndex === -1 || closeParenIndex < openParenIndex ? value.length : closeParenIndex;
		return value.slice(openParenIndex + 1, endIndex).trim();
	}

	getValue(proposition: ItemValue) {
		const { code, extraField, translation: rawTranslation } = proposition;
		const translation = (rawTranslation ?? '').trim();
		if (code === undefined && !translation && extraField === undefined) {
			return '';
		}
		if (code === '') {
			return this.filter?.noValueLabel ?? this.NO_VALUE_LABEL;
		}
		if (extraField !== undefined) {
			const extra = extraField.trim();
			return extra ? `${ translation } (${ extra })` : translation || '';
		}

		if (code !== undefined) {
			return `${ translation } (${ this.truncateZeros(code) })`;
		}

		return translation || '';
	}

	propositionChanged(proposition: ItemValue) {
		if (!this.filterValue) {
			return;
		}
		Object.assign(this.filterValue, proposition);
	}

	private clearSelectionOnManualInput(query: string) {
		if (!this.filterValue) {
			return;
		}
		const typedValue = query.trim();
		if (!typedValue) {
			this.filterValue.code = undefined;
			this.filterValue.translation = undefined;
			this.filterValue.extraField = undefined;
			return;
		}
		const selectedValue = this.getValue(this.filterValue).trim();
		if (selectedValue && typedValue !== selectedValue) {
			this.filterValue.code = undefined;
			this.filterValue.translation = undefined;
			this.filterValue.extraField = undefined;
		}
	}
}
